import { useState } from "react";
import {
  AlertTriangle,
  Bot,
  ChevronDown,
  Gauge,
  GraduationCap,
  ListChecks,
  TrendingDown,
  TrendingUp,
} from "lucide-react";
import { fetchTutorQuality, type Range, type TutorQuality } from "../../api/insights";
import { Card, Pill, Stat } from "../../components/ui";
import { LineChart, RankBars } from "./charts";
import { Loading, useSection } from "./section";

const CRITERIA: { key: keyof TutorQuality["scores"]; label: string; hint: string }[] = [
  { key: "correctness", label: "Correctness", hint: "Facts, working and the answer are right" },
  { key: "relevance", label: "Relevance", hint: "Answers what the student asked" },
  { key: "clarity", label: "Clarity", hint: "Clear, at a secondary-school level" },
  { key: "teaching", label: "Teaching", hint: "Guides the student, not just the answer" },
  { key: "tone", label: "Tone", hint: "Encouraging and right for a minor" },
];

const SEVERITY: Record<
  "high" | "medium" | "low",
  { label: string; tone: "negative" | "warning" | "muted" }
> = {
  high: { label: "Wrong or misleading", tone: "negative" },
  medium: { label: "Unhelpful", tone: "warning" },
  low: { label: "Polish", tone: "muted" },
};

/** "+4 vs previous", in points; nothing without an earlier period. */
function Delta({
  now,
  before,
  format,
  better,
}: {
  now: number | null;
  before: number | null;
  format: (n: number) => string;
  better: "up" | "down";
}) {
  if (now === null || before === null) {
    return <span className="text-text-muted">No earlier period to compare</span>;
  }
  const diff = now - before;
  const good = better === "up" ? diff >= 0 : diff <= 0;
  const Icon = diff >= 0 ? TrendingUp : TrendingDown;
  return (
    <span className={`inline-flex items-center gap-1 ${good ? "text-positive" : "text-negative"}`}>
      <Icon size={13} strokeWidth={2} aria-hidden="true" />
      {diff >= 0 ? "+" : ""}
      {format(diff)} <span className="text-text-muted">vs previous</span>
    </span>
  );
}

const points = (n: number) => `${Math.round(n)}`;
const share = (n: number) => `${Math.round(n * 100)} pts`;

/**
 * How well the AI tutor is answering students.
 *
 * The AI engine grades every tutor conversation itself, with a separate judge
 * model, in the background after each reply, and saves it. This only reads
 * those gradings. Each reply is scored on five criteria, with any problems.
 * The flagged ones are listed worst first, with the conversation, so product
 * can read what went wrong and fix the tutor's prompts.
 */
export function TutorQualitySection({ range }: { range: Range }) {
  const { data, error } = useSection<TutorQuality>(() => fetchTutorQuality(range), [range]);
  if (!data) return <Loading error={error} />;

  // The engine keeps a conversation for a month, so a longer view sees less.
  const kept =
    data.kept_days && data.range > data.kept_days
      ? ` Conversations are kept for ${data.kept_days} days, so this covers the last ${data.kept_days}.`
      : "";

  if (!data.graded) {
    return (
      <Card title="Tutor quality" icon={Bot}>
        <p className="text-[13px] text-text-muted">
          No tutor conversations graded in this period yet. The AI engine grades each one within
          seconds of a tutor reply.{kept} If this stays empty while students use the tutor, check
          the AI engine has its TUTOR_EVAL settings.
        </p>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-[11px] text-text-muted">
        Graded by the AI engine with a separate judge model, not the tutor itself, after every tutor
        reply.{kept}
        {data.truncated
          ? " Only the most recent gradings are counted: there were too many to read."
          : ""}
      </p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Stat
          label="Average score"
          value={data.score.value === null ? "—" : `${Math.round(data.score.value)} / 100`}
          icon={Gauge}
          tone="info"
          hint={
            <Delta
              now={data.score.value}
              before={data.score.previous}
              format={points}
              better="up"
            />
          }
        />
        <Stat
          label="Need a person to look"
          value={
            data.needs_attention.value === null
              ? "—"
              : `${Math.round(data.needs_attention.value * 100)}%`
          }
          icon={AlertTriangle}
          tone="warning"
          hint={
            <Delta
              now={data.needs_attention.value}
              before={data.needs_attention.previous}
              format={share}
              better="down"
            />
          }
        />
        <Stat
          label="Conversations graded"
          value={data.graded.toLocaleString("en-NG")}
          icon={ListChecks}
          tone="positive"
          hint={
            <span className="text-text-muted">
              {data.problems.high} wrong or misleading · {data.problems.medium} unhelpful
            </span>
          }
        />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[2fr_1fr]">
        <Card title="Average score by day" subtitle="Out of 100" icon={TrendingUp}>
          <LineChart
            label="Average tutor score"
            points={data.daily.map((d) => ({ date: d.date, value: d.score }))}
            format={points}
          />
        </Card>
        <Card
          title="By criterion"
          subtitle="Average, 1 (poor) to 5 (excellent)"
          icon={GraduationCap}
        >
          <ul className="flex flex-col gap-3">
            {CRITERIA.map((c) => {
              const v = data.scores[c.key];
              return (
                <li key={c.key} title={c.hint}>
                  <div className="flex items-center justify-between text-[13px]">
                    <span>{c.label}</span>
                    <span className="tabular-nums text-text-muted">
                      {v === null ? "—" : v.toFixed(1)}
                    </span>
                  </div>
                  <div className="mt-1 h-2 overflow-hidden rounded bg-surface-high">
                    <div
                      className={`h-full rounded ${v !== null && v < 3 ? "bg-negative" : v !== null && v < 4 ? "bg-warning" : "bg-positive"}`}
                      style={{ width: `${v === null ? 0 : ((v - 1) / 4) * 100}%` }}
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        </Card>
      </div>

      {data.categories.length ? (
        <Card
          title="Where the problems are"
          subtitle="Problems found, by criterion"
          icon={ListChecks}
        >
          <RankBars items={data.categories.map((c) => ({ key: c.category, value: c.count }))} />
        </Card>
      ) : null}

      <Card
        title="Conversations to look at"
        subtitle="Flagged by the judge, lowest score first"
        icon={AlertTriangle}
      >
        {data.worst.length ? (
          <ul className="flex flex-col gap-2">
            {data.worst.map((w) => (
              <Conversation key={w.id} item={w} />
            ))}
          </ul>
        ) : (
          <p className="text-[13px] text-text-muted">
            Nothing flagged in this period: no wrong answers, and no conversation under 60.
          </p>
        )}
      </Card>
    </div>
  );
}

function Conversation({ item }: { item: TutorQuality["worst"][number] }) {
  const [open, setOpen] = useState(false);
  const question = item.question?.replace(/^QUESTION:\s*/, "") ?? null;
  return (
    <li className="rounded-lg border border-border">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className="flex w-full items-start gap-3 px-3 py-2 text-left"
      >
        <span
          className={`mt-0.5 shrink-0 rounded px-2 py-0.5 text-[12px] font-semibold tabular-nums ${item.score < 40 ? "bg-negative/15 text-negative" : "bg-warning/15 text-warning"}`}
        >
          {item.score}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[13px]">{question ?? "Question not recorded"}</span>
          <span className="block text-[12px] text-text-muted">{item.summary}</span>
        </span>
        <ChevronDown
          size={14}
          aria-hidden="true"
          className={`mt-1 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open ? (
        <div className="flex flex-col gap-3 border-t border-border px-3 py-3 text-[13px]">
          {item.problems.length ? (
            <ul className="flex flex-col gap-2">
              {item.problems.map((p, i) => (
                <li key={i} className="flex flex-col gap-0.5">
                  <span className="flex flex-wrap items-center gap-2">
                    <Pill tone={SEVERITY[p.severity].tone}>{SEVERITY[p.severity].label}</Pill>
                    <span className="text-[12px] text-text-muted">
                      Reply {p.turn} · {p.category}
                    </span>
                  </span>
                  <span>{p.description}</span>
                  <span className="text-text-muted">Should have: {p.suggestion}</span>
                </li>
              ))}
            </ul>
          ) : null}
          <ol className="flex flex-col gap-2 border-t border-border pt-3">
            {item.turns.map((t) => (
              <li key={t.turn} className="flex flex-col gap-1">
                <span className="text-[12px] text-text-muted">Student: {t.student || "—"}</span>
                <span className="whitespace-pre-line rounded-md bg-surface-high px-2 py-1.5">
                  {t.tutor}
                </span>
                <span className="text-[12px] text-text-muted">
                  Reply {t.turn} · {t.score}/100 · {t.comment}
                </span>
              </li>
            ))}
          </ol>
          <p className="text-[11px] text-text-muted">
            Graded {new Date(item.evaluated_at).toLocaleString("en-GB")}. Question id{" "}
            {item.question_id}.
          </p>
        </div>
      ) : null}
    </li>
  );
}
